/**
 * Подвесной потолок комнаты на 2D-чертеже — компонент раздела каталога.
 *
 * На плане потолков вид читается по штриховке, как на рабочих чертежах:
 * гипсокартон — косая сетка, натяжной — редкие точки, у глянцевого поверх точек
 * ещё короткие косые блики. Заливка — цветом потолка; по периметру пунктир
 * отступом внутрь — багет натяжного или профиль каркаса гипсокартона.
 */
import { CeilingMaterial, type CeilingLook } from "@/shared/catalog";
import type { Room } from "@/widgets/plan";
import { DrywallHatch } from "./DrywallHatch";
import { StretchGlossyHatch } from "./StretchGlossyHatch";
import { StretchMatteHatch } from "./StretchMatteHatch";
import { CEILING_HATCH_COLOR } from "./ceilingHatchGeometry";

interface Props {
	room: Room;
	look: CeilingLook;
	mmPerPx: number;
}

export const CeilingShape = ({ room, look, mmPerPx }: Props) => {
	const clipId = `room-ceiling-${room.id}-clip`;
	const points = room.inner.map((p) => `${p.x},${p.y}`).join(" ");
	const dash = 6 * mmPerPx;
	const hatch =
		look.material === CeilingMaterial.Drywall ? (
			<DrywallHatch roomId={room.id} points={points} mmPerPx={mmPerPx} />
		) : look.material === CeilingMaterial.StretchGlossy ? (
			<StretchGlossyHatch room={room} clipId={clipId} />
		) : (
			<StretchMatteHatch room={room} clipId={clipId} />
		);

	return (
		<g pointerEvents="none">
			<defs>
				<clipPath id={clipId}>
					<polygon points={points} />
				</clipPath>
			</defs>
			<polygon points={points} fill={look.color} />
			{hatch}
			{/* Багет или профиль по периметру: обводка двойной толщины, обрезанная
			    контуром комнаты, — видна только внутренняя половина */}
			<polygon
				points={points}
				fill="none"
				stroke={CEILING_HATCH_COLOR}
				strokeWidth={8 * mmPerPx}
				strokeDasharray={`${dash} ${dash}`}
				opacity="0.6"
				clipPath={`url(#${clipId})`}
			/>
		</g>
	);
};
